<template>
  <div class="forgot-password-form">
    <h1 class="form-title">{{ t('auth.forgot.title') }}</h1>
    <p class="form-text">
      {{ t('auth.forgot.text') }}
    </p>

    <form class="form-container" @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="email" class="form-label">{{ t('auth.forgot.label.email') }}</label>
        <input
          id="email"
          name="email"
          v-model="formData.email"
          type="text"
          :class="['form-control', { 'form-control-error': errors.email || apiError }]"
          :placeholder="t('form.placeholder.email')"
          @input="validateEmail"
        />
        <p :class="['error-message', { 'error-visible': errors.email }]">{{ errors.email || '&nbsp;' }}</p>
      </div>

      <div v-if="apiError" class="alert alert-danger">
        {{ t('apiCodes.' + apiError) }}
      </div>

      <div v-if="successMessage" class="alert alert-success">
        {{ t('apiCodes.' + successMessage) }}
      </div>

      <button
        type="submit"
        class="btn-submit"
        :disabled="isLoading || successMessage || !isEmailValid"
      >
        <span v-if="isLoading" class="spinner"></span>
        {{ isLoading ? t('header.user.logging_in') : t('auth.forgot.btn') }}
      </button>
    </form>

    <!-- Legal notice for reCAPTCHA -->
    <div class="recaptcha-notice" v-html="t('form.recaptcha_notice', { 
      privacy: `<a href='https://policies.google.com/privacy' target='_blank'>${t('form.google_privacy')}</a>`, 
      terms: `<a href='https://policies.google.com/terms' target='_blank'>${t('form.google_terms')}</a>` 
    })"></div>

    <div class="form-footer">
      <router-link to="/login" class="link-back">{{ t('auth.forgot.back_link') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useConfigStore } from '@/stores/configStore'
import { useLanguage } from '@/composables/useLanguage'
import { useToast } from '@/composables/useToast'

const { t, currentLanguage } = useLanguage()
const configStore = useConfigStore()

// Re-validate on language change to update messages
watch(currentLanguage, () => {
  if (errors.value.email) validateEmail()
})
const toast = useToast()
const router = useRouter()
const authStore = useAuthStore()
const isLoading = ref(false)
const apiError = ref('')
const successMessage = ref('')

const formData = ref({
  email: ''
})

const errors = ref({
  email: ''
})

const isEmailValid = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return formData.value.email && emailRegex.test(formData.value.email)
})

onMounted(() => {
  if (authStore.isAuthenticated) {
    router.push('/')
  }
})

const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!formData.value.email) {
    errors.value.email = t('form.error.required')
  } else if (!emailRegex.test(formData.value.email)) {
    errors.value.email = t('form.error.email_invalid')
  } else {
    errors.value.email = ''
  }
}

const generateRecaptchaToken = async () => {
  if (!configStore.recaptchaSiteKey) return ''
  
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        resolve(true)
        return
      }
      
      let attempts = 0
      const interval = setInterval(() => {
        attempts++
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval)
          resolve(true)
        } else if (attempts > 50) {
          clearInterval(interval)
          resolve(false)
        }
      }, 100)
    })
  }

  const isRecaptchaReady = await waitForRecaptcha()
  if (!isRecaptchaReady) return ''

  return await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action: 'forgot_password' })
        resolve(token)
      } catch (e) {
        console.error('[RECAPTCHA] Execute error:', e)
        resolve('')
      }
    })
  })
}

const handleSubmit = async () => {
  validateEmail()

  if (errors.value.email) {
    return
  }

  apiError.value = ''
  successMessage.value = ''
  isLoading.value = true

  try {
    const recaptchaToken = await generateRecaptchaToken()
    if (!recaptchaToken) {
      apiError.value = 'AUTH_RECAPTCHA_REQUIRED'
      isLoading.value = false
      return
    }
    const result = await authStore.forgotPassword(formData.value.email, recaptchaToken)
    if (result && result.success) {
      successMessage.value = 'AUTH_FORGOT_SUCCESS'
      toast.success(t('toast.success.email_sent'))
      router.push('/forgot-password/success')
    } else {
      const code = result?.code || 'SYSTEM_ERROR'
      apiError.value = code
    }
  } catch (err) {
    const code = err?.response?.data?.code || 'SYSTEM_ERROR'
    apiError.value = code
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.forgot-password-form {
  position: relative;
  z-index: 10;
}

.form-title {
  text-align: center;
  font-family: 'Montserrat', sans-serif;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #000000;
  margin-bottom: 12px;
  text-transform: uppercase;
}

.form-text {
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #666666;
  margin-bottom: 28px;
  line-height: 1.6;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #444444;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.form-control {
  width: 100%;
  padding: 13px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #000000;
  border: 1.5px solid #e5e5e5;
  border-radius: 6px;
  background-color: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--dark-teal);
  box-shadow: 0 0 0 4px rgba(0,140,149,0.12);
}

.form-control::placeholder {
  color: #bbbbbb;
}

.form-control:hover {
  border-color: #cccccc;
}

.form-control-error {
  border-color: #d9534f !important;
}

.error-message {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: #d9534f;
  margin-top: 6px;
  font-weight: 600;
  height: 14px;
  opacity: 0;
  transition: opacity 0.3s ease;
  letter-spacing: 0.3px;
}

.error-message.error-visible {
  opacity: 1;
}

.alert {
  padding: 14px;
  margin-bottom: 24px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid #d9534f;
  color: #d9534f;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
  background: rgba(217, 83, 79, 0.05);
  border-radius: 6px;
}

.alert-success {
  border-color: var(--dark-teal);
  color: var(--dark-teal);
  background: rgba(0,140,149,0.05);
}

.btn-submit {
  width: 100%;
  padding: 15px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background: #000000;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
}

.btn-submit:hover:not(:disabled) {
  background: var(--dark-teal);
  box-shadow: 0 8px 20px -6px rgba(0,140,149,0.5);
}

.btn-submit:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-submit:disabled {
  background: #5C8889;
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.recaptcha-notice {
  text-align: center;
  font-size: 11px;
  line-height: 18px;
  color: #888888;
  margin: 16px 0 22px;
}

.recaptcha-notice a {
  color: #888888;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.recaptcha-notice a:hover {
  color: var(--dark-teal);
}

.form-footer {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.form-footer p {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #888888;
  margin: 0;
}

.link-back {
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--dark-teal);
  text-decoration: none;
  position: relative;
  padding-bottom: 2px;
  transition: color 0.2s ease;
}

.link-back::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: -1px;
  height: 1.5px;
  background: var(--bright-teal);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.25s ease;
}

.link-back:hover {
  color: var(--bright-teal);
}

.link-back:hover::after {
  transform: scaleX(1);
}

@media (max-width: 600px) {
  .form-title {
    font-size: 18px;
    margin-bottom: 16px;
  }

  .btn-submit {
    padding: 14px;
    font-size: 13px;
  }
}
</style>
